/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import { Component, OnDestroy } from '@angular/core';
import { L10nService } from '@vmw/ngx-vip';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { Subject } from 'rxjs';
import { BOTTOM_RIGHT_ALIGNED_CONNECTED_POSITION } from 'ng/shared/components/avi-dropdown-button';
import { ITrafficLogsStates, TrafficLogsStore } from '../traffic-logs.store';
import { LogsDownloadService } from '../../../services/logs-download.service';
import * as l10n from './traffic-logs-actions.l10n';
import './traffic-logs-actions.component.less';
import { LogsRefreshService } from '../../../services/logs-refresh.service';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Component housing Refresh and Export actions for the Logs Page.
 * @author Alex Klyuev, Yogesh Patil
 */
@Component({
    selector: 'traffic-logs-actions',
    templateUrl: './traffic-logs-actions.component.html',
})
export class TrafficLogsActionsComponent implements OnDestroy {
    public readonly l10nKeys = l10nKeys;

    /**
     * Position definition of the export options panel relative to the export button.
     */
    public readonly exportOptionsPanelPosition: [ConnectedPosition] = [
        BOTTOM_RIGHT_ALIGNED_CONNECTED_POSITION,
    ];

    /**
     * Subject to control closing the export options panel. Subscribed to by avi-tooltip.
     */
    public readonly exportOptionsPanelCloseControl$ = new Subject<boolean>();

    constructor(
        public readonly trafficLogsStore: TrafficLogsStore<ITrafficLogsStates>,
        private readonly logsRefreshService: LogsRefreshService,
        private readonly logsDownloadService: LogsDownloadService,
        l10nService: L10nService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnDestroy(): void {
        this.exportOptionsPanelCloseControl$.complete();
    }

    /**
     * Refresh the logs page while preserving exisiting configs/filters, in case there are new logs.
     */
    public refreshPage(): void {
        this.logsRefreshService.refresh();
    }

    /**
     * Download all logs on the current page of the table.
     */
    public downloadCurrentPage(): void {
        this.logsDownloadService.downloadCurrentPage();
        this.closeExportOptionsPanel();
    }

    /**
     * Download all logs for the given set of parameters.
     */
    public downloadAllLogs(): void {
        this.logsDownloadService.downloadAllLogs();
        this.closeExportOptionsPanel();
    }

    /**
     * Download any logs that have been selected by the user.
     */
    public downloadSelectedLogs(): void {
        this.logsDownloadService.downloadSelectedLogs();
        this.closeExportOptionsPanel();
    }

    /**
     * Close the Export dropdown panel.
     */
    private closeExportOptionsPanel(): void {
        this.exportOptionsPanelCloseControl$.next(false);
    }
}
